import { useEffect, useMemo, useRef, useState } from "react";
import { Check, X, RotateCcw, Shuffle } from "lucide-react";

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

const Slot = ({ b, i, placed, result, onDrop, onClick, onRemove }) => {
  const state = result ? (result[i] ? "bg-emerald-300" : "bg-red-300") : placed ? "bg-[var(--sun)]" : "bg-white border-dashed";
  return (
    <div className="ink-box-sm rounded-2xl bg-[var(--paper)] p-3" data-testid={`map-quiz-slot-${i}`}>
      {b.link && <p className="text-center text-[11px] font-extrabold italic text-neutral-700">{b.link}</p>}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onDrop(i, Number(e.dataTransfer.getData("text/plain"))); }}
        onClick={() => (placed != null && !result ? onRemove(i) : onClick(i))}
        data-testid={`map-quiz-drop-${i}`}
        className={`font-display my-1 flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-[var(--ink)] px-3 text-center font-bold transition-colors ${state}`}
      >
        {placed != null ? placed.label : <span className="text-sm font-semibold text-neutral-400">Trascina qui</span>}
        {result && (result[i] ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />)}
      </div>
      <ul className="mt-1 space-y-1 text-xs">
        {b.children.map((c, j) => <li key={j} className="rounded-lg border-2 border-[var(--ink)] bg-white px-2 py-1 font-semibold">{c}</li>)}
      </ul>
      {result && !result[i] && <p className="mt-1 text-xs font-extrabold text-emerald-700">Giusto: {b.label}</p>}
    </div>
  );
};

export const MapQuiz = ({ terms, onFinish }) => {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * terms.length));
  const [round, setRound] = useState(0);
  const term = terms[Math.min(idx, terms.length - 1)];
  const branches = term.concept_map.branches;
  const chips = useMemo(() => shuffle(branches.map((b, i) => ({ id: i, label: b.label }))), [branches, round]); // eslint-disable-line react-hooks/exhaustive-deps
  const [slots, setSlots] = useState({});
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const sent = useRef(false);

  useEffect(() => { setSlots({}); setSelected(null); setResult(null); sent.current = false; }, [idx, round]);

  const nextMap = () => {
    if (terms.length > 1) {
      let n = idx;
      while (n === idx) n = Math.floor(Math.random() * terms.length);
      setIdx(n);
    }
    setRound((r) => r + 1);
  };

  const usedIds = new Set(Object.values(slots));
  const place = (slot, chipId) => {
    if (result || Number.isNaN(chipId)) return;
    setSlots((s) => {
      const next = Object.fromEntries(Object.entries(s).filter(([, v]) => v !== chipId));
      next[slot] = chipId;
      return next;
    });
    setSelected(null);
  };
  const remove = (slot) => setSlots((s) => Object.fromEntries(Object.entries(s).filter(([k]) => Number(k) !== slot)));

  const check = () => {
    const r = branches.map((b, i) => slots[i] != null && branches[slots[i]].label.trim().toLowerCase() === b.label.trim().toLowerCase());
    setResult(r);
    if (!sent.current) {
      sent.current = true;
      onFinish?.(r.filter(Boolean).length, branches.length);
    }
  };

  const score = result?.filter(Boolean).length;
  return (
    <div className="mx-auto max-w-4xl" data-testid="map-quiz">
      <p className="mb-4 text-center text-sm font-bold text-neutral-600">Trascina ogni concetto nel posto giusto (oppure toccalo e poi tocca lo spazio). Usa le idee collegate come indizi! Conta il primo tentativo.</p>
      <div className="sticky top-[66px] z-20 -mx-2 mb-6 flex flex-wrap justify-center gap-2 rounded-2xl bg-[var(--paper)]/95 px-2 py-3 backdrop-blur-md" data-testid="map-quiz-chips">
        {chips.filter((c) => !usedIds.has(c.id)).map((c) => (
          <button
            key={c.id}
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/plain", String(c.id))}
            onClick={() => setSelected(selected === c.id ? null : c.id)}
            data-testid={`map-quiz-chip-${c.id}`}
            className={`ink-box-sm font-display cursor-grab rounded-full px-4 py-2 font-bold transition-transform active:cursor-grabbing ${selected === c.id ? "-translate-y-1 bg-[var(--ink)] text-white" : "bg-white hover:-translate-y-0.5"}`}
          >
            {c.label}
          </button>
        ))}
        {!result && usedIds.size === branches.length && <span className="text-sm font-extrabold text-emerald-700">Tutti piazzati! Controlla le risposte.</span>}
      </div>
      <div className="flex flex-col items-center">
        <div className="ink-box rounded-full bg-[var(--ink)] px-8 py-3 text-white"><span className="font-display text-2xl font-bold" data-testid="map-quiz-center">{term.concept_map.center}</span></div>
        <div className="h-5 w-[3px] bg-[var(--ink)]" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {branches.map((b, i) => (
          <Slot key={i} b={b} i={i} placed={slots[i] != null ? chips.find((c) => c.id === slots[i]) : null} result={result} onDrop={place} onClick={(s) => selected != null && place(s, selected)} onRemove={remove} />
        ))}
      </div>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        {!result ? (
          <button onClick={check} disabled={usedIds.size < branches.length} data-testid="map-quiz-check" className="ink-box-sm ink-press rounded-full bg-[var(--sky)] px-6 py-3 font-extrabold text-white disabled:opacity-50">Controlla</button>
        ) : (
          <>
            <p className="font-display text-2xl font-bold" data-testid="map-quiz-score">{score} / {branches.length} {score === branches.length ? "Perfetto!" : ""}</p>
            <button onClick={() => { setSlots({}); setResult(null); }} data-testid="map-quiz-retry" className="ink-box-sm ink-press flex items-center gap-2 rounded-full bg-white px-5 py-2 font-extrabold"><RotateCcw className="h-4 w-4" /> Riprova</button>
            <button onClick={nextMap} data-testid="map-quiz-next" className="ink-box-sm ink-press flex items-center gap-2 rounded-full bg-[var(--sun)] px-5 py-2 font-extrabold"><Shuffle className="h-4 w-4" /> Altra mappa</button>
          </>
        )}
      </div>
    </div>
  );
};
